import { useKeycloakToken } from '../common/useKeycloak';
import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import SourcesList, { sourceSortValues } from './SourcesList';
import { SourceWithRoutes } from '../common/ObjectTypes';
import { useAllOrganizations } from '../../api/organizations/useAllOrganizations';
import { page_size } from '../../api/apiUtils';
import loadSources from '../../api/sources/loadSources';
import { getRoutesByIntegration } from '../../api/routes/routesApi';
import {
  useDestinationTypes,
  useDestinationUrls,
  useProviderTypes,
} from '../../api/useFilterOptions';
import { useListQuery } from '../../api/useListQuery';
import { queryKeys } from '../../api/queryKeys';
import { useKeycloak } from '@react-keycloak/web';
import { toast } from 'react-toastify';
import { useTranslation } from 'react-i18next';

const SourcesPage = () => {
  const { t } = useTranslation();
  type FilterTypes = {
    provider_type__in: FilterTags[];
    destination_url__in: FilterTags[];
    destination_type__in: FilterTags[];
    owner__in: FilterTags[];
    ordering: string;
    search: string;
    offset: number;
  };
  type FilterTags = {
    name: string;
    id: string;
  };
  const defaultSourceFilter: FilterTypes = {
    provider_type__in: [],
    destination_url__in: [],
    destination_type__in: [],
    owner__in: [],
    ordering: '-external_id',
    search: '',
    offset: 0,
  };
  const navigate = useNavigate();
  const [token, showLoginModal, setShowLoginModal] = useKeycloakToken();
  const { keycloak } = useKeycloak();
  const { organizations, loadingOrganizations } = useAllOrganizations();
  const { destinationTypes, loadingDestinationTypes } = useDestinationTypes();
  const { providerTypes, loadingProviderTypes } = useProviderTypes();
  const { destinationUrls, loadingDestinationUrls } = useDestinationUrls();
  const [sourceFilter, setSourceFilter] = useState<FilterTypes>(() => {
    const savedFilter = sessionStorage.getItem('sourceFilter');
    const filter = savedFilter
      ? { ...defaultSourceFilter, ...JSON.parse(savedFilter) }
      : defaultSourceFilter;
    // Persisted ordering may predate the Sort By dropdown (old column-header
    // sorts saved e.g. integration__id) — reset it so the dropdown's
    // displayed selection always matches the API ordering.
    if (!sourceSortValues.includes(filter.ordering))
      filter.ordering = defaultSourceFilter.ordering;
    return filter;
  });

  const sourcesList = useListQuery<SourceWithRoutes>(
    queryKeys.sources.list(sourceFilter),
    async (token) => {
      const [source_results, total_sources, error] = await loadSources(
        token,
        '',
        sourceFilter,
      );
      if (error) throw error;
      return {
        results: source_results.map((source) => ({ source, routes: [] })),
        count: total_sources,
      };
    },
  );

  useEffect(() => {
    sessionStorage.setItem('sourceFilter', JSON.stringify(sourceFilter));
  }, [sourceFilter]);

  const handlePageClick = (e: any) => {
    setSourceFilter((prev) => ({ ...prev, offset: e.selected * page_size }));
  };

  const handleGlobalSearch = (searchSubStr: string) => {
    setSourceFilter((prev) => ({ ...prev, search: searchSubStr, offset: 0 }));
  };

  const handleFilterOwner = (selected: any) => {
    const tags: FilterTags[] = selected
      ? [{ name: selected.label, id: selected.value }]
      : [];
    setSourceFilter((prev) => ({ ...prev, owner__in: tags, offset: 0 }));
  };

  const handleFilterEndpoint = (selected: any) => {
    const tags: FilterTags[] = selected
      ? [{ name: selected.label, id: selected.value }]
      : [];
    setSourceFilter((prev) => ({
      ...prev,
      destination_url__in: tags,
      offset: 0,
    }));
  };

  const handleFilterProviderType = (selected: any) => {
    const tags: FilterTags[] = selected
      ? [{ name: selected.label, id: selected.value }]
      : [];
    setSourceFilter((prev) => ({
      ...prev,
      provider_type__in: tags,
      offset: 0,
    }));
  };

  const handleFilterDestinationType = (selected: any) => {
    const tags: FilterTags[] = selected
      ? [{ name: selected.label, id: selected.value }]
      : [];
    setSourceFilter((prev) => ({
      ...prev,
      destination_type__in: tags,
      offset: 0,
    }));
  };

  const handleSortBy = (selected: { label: string; value: string }) => {
    setSourceFilter((prev) => ({
      ...prev,
      ordering: selected.value,
      offset: 0,
    }));
  };

  const handleToggleDestinationDetailsModal = async (dest_id: string) => {
    if (
      dest_id &&
      token &&
      typeof token === 'string' &&
      !keycloak.isTokenExpired()
    ) {
      navigate(`/sources/destination/${dest_id}/configuration`, {
        replace: false,
      });
    }
  };

  // Sources rows carry the provider integration's id, which is exactly what a
  // route URL takes — but a provider with no Route has no Route page, so the
  // lookup still runs to tell a working link from a dead one. Cached per
  // integration: the same row can be clicked repeatedly, and a second click
  // while the first lookup is in flight must not fire another query.
  const providersWithRoute = useRef<Set<string>>(new Set());
  const resolvingRoute = useRef<Set<string>>(new Set());
  const handleViewRouteDetails = async (integration_id: string) => {
    if (!(keycloak.token && !keycloak.isTokenExpired())) return;
    if (providersWithRoute.current.has(integration_id)) {
      return navigate(`/routes/${integration_id}`, { replace: false });
    }
    if (resolvingRoute.current.has(integration_id)) return;
    resolvingRoute.current.add(integration_id);
    try {
      const data = await getRoutesByIntegration(
        keycloak.token,
        'provider',
        integration_id,
      );
      if (data.results?.length) {
        providersWithRoute.current.add(integration_id);
        navigate(`/routes/${integration_id}`, { replace: false });
        return;
      }
      // Silently doing nothing reads as a broken link, so say which it is.
      toast.error(
        data.ok
          ? t("This source's provider has no Route yet.")
          : t('Something went wrong. Please try again or contact support.'),
        { position: toast.POSITION.TOP_RIGHT },
      );
    } finally {
      resolvingRoute.current.delete(integration_id);
    }
  };

  const toSingleValue = (tags: FilterTags[]) =>
    tags.length > 0 ? { label: tags[0].name, value: tags[0].id } : null;

  const endpointSelectValue = toSingleValue(sourceFilter.destination_url__in);
  const ownerSelectValue = toSingleValue(sourceFilter.owner__in);
  const providerTypeSelectValue = toSingleValue(sourceFilter.provider_type__in);
  const destinationTypeSelectValue = toSingleValue(
    sourceFilter.destination_type__in,
  );

  return (
    <div>
      <SourcesList
        sources={sourcesList.results}
        totalCount={sourcesList.count}
        take={page_size}
        offset={sourceFilter.offset}
        onPageClicked={handlePageClick}
        loading={sourcesList.loading}
        organizations={organizations}
        loadingOrganizations={loadingOrganizations}
        destinationTypes={destinationTypes}
        loadingDestinationTypes={loadingDestinationTypes}
        destinationUrls={destinationUrls}
        loadingDestinationUrls={loadingDestinationUrls}
        providerTypes={providerTypes}
        loadingProviderTypes={loadingProviderTypes}
        globalSearchStr={sourceFilter.search}
        onChangeGlobalSearch={handleGlobalSearch}
        onRefresh={() => sourcesList.refresh()}
        onChangeOwner={handleFilterOwner}
        ownerFilterValue={ownerSelectValue}
        onChangeEndpoint={handleFilterEndpoint}
        endpointFilterValue={endpointSelectValue}
        onChangeProviderType={handleFilterProviderType}
        providerTypeFilterValue={providerTypeSelectValue}
        onChangeDestinationType={handleFilterDestinationType}
        destinationTypeFilterValue={destinationTypeSelectValue}
        ordering={sourceFilter.ordering}
        onChangeSortBy={handleSortBy}
        onViewDetails={handleToggleDestinationDetailsModal}
        onViewRouteDetails={handleViewRouteDetails}
      />
    </div>
  );
};

export default SourcesPage;
